/**
 * Background auto-sync policy for pushing queued local orders to the server
 * without waiting for close shift.
 */
export const AUTO_SYNC = {
  enabled: true,
  /** Periodic drain while the POS session is active (online). */
  intervalMs: 120_000,
  /** Debounce rapid payment completions before a single drain. */
  debounceAfterCompleteMs: 5_000,
  /** Per-trigger debounce; falls back to debounceAfterCompleteMs for after_complete. */
  debounceMsByReason: {
    interval: 0,
    online: 1_000,
    after_complete: 5_000,
    after_adjustment: 5_000,
    manual: 0,
    visibility: 2_000,
  },
  drainOnOnline: true,
  /** When true, failed local orders are included in auto drain (not in PR1 default). */
  includeFailed: false,
  maxAutoRetries: 3,
} as const;

/**
 * Shrinks synced local orders on disk after they are safely on the server.
 */
export const LOCAL_COMPACTION = {
  enabled: true,
  /** Minimum time since last update before compacting (default 1 hour). */
  minAgeMs: 60 * 60 * 1000,
  /** Run compaction after a successful background auto-sync drain. */
  runAfterAutoSync: true,
  /** Remove operation log rows that are already synced. */
  deleteSyncedOperations: true,
  /** Remove local KDS ticket payloads for compacted orders. */
  deleteKitchenTickets: true,
} as const;

export type AutoSyncReason =
  | "interval"
  | "online"
  | "after_complete"
  | "after_adjustment"
  | "manual"
  | "visibility";

export function getAutoSyncDebounceMs(
  reason: AutoSyncReason,
  policy: Pick<typeof AUTO_SYNC, "debounceMsByReason"> = AUTO_SYNC,
): number {
  return policy.debounceMsByReason[reason] ?? 0;
}
